import {Request} from "../data/Request";
import {Utils} from "../common/Utils";
import {Component} from "../view/Component";
import {UserIdentity} from "./UserIdentity";
import {BaseClass} from "./BaseClass";
import Const from "./Const";

/**
 * Default class to be used for user identification.
 * @type {UserIdentity}
 */
const DEFAULT_USER_CLASS = UserIdentity;

/**
 * Base frontend application class that handles configuration, authentication, redirects
 * and component instantiation.
 *
 * The class is the entry point for the frontend webapp.
 *
 * During initialization all registered components will be automatically instantiated. To register a
 * component in the application it can be registered manually by using {@link registerComponents} or
 * define the "components" option in {@link ApplicationOptions}.
 *
 * @example <caption>Auto component instantiation</caption>
 * // Create an index.js file that contains all components (e.g. in directory "/components")
 * export * from './FooBar';
 *
 * // create a new Application instance that references these components for auto instantiation
 * import * as components from './components'; // contains all component classes
 *
 * let app = new Application({
 *  components: {
 *    components: components // register all components in namespace "components."
 *  }
 * });
 *
 * // HTML DOM element to be used as container for FooBar class.
 * // This element will be automatically passed to FooBar in "config.el"
 * <div data-component="components.foobar"></div>
 *
 *
 */
export class Application extends Component {

  /**
   * Configuration object for the {@link Application} class.
   * This is a child configuration object of {@link ComponentConfig}.
   * @typedef {ComponentConfig} ApplicationOptions
   * @property {jQuery|String} [autoInit=false] - initialize automatically the application otherwise manually with {@link init}
   * @property {jQuery|String} [el] - application DOM container: jQuery element or selector, default: <html> dom node
   * @property {Object<RegExp,String>|Function} [access] - access rules or a function with parameter path and current user
   * @property {Object<String,Array<Component>>} [components] - components to be automatically registered
   * @property {ComponentConfig} config - application config
   * @property {UserIdentity} [config.userClass=User] - User class that handles authentication, register,... Default: {@link UserIdentity}
   * @property {String|Object<String,String>} [config.homeUrl=/] - global home url (string) or role based (object) or function (parameter user) that returns the URL string
   * @property {String} [config.loginUrl=config.homeUrl] - global login url
   */

  /**
   * Instantiate the application and set all configuration stuff,
   * like component registration, access rules, user's base class
   * and custom global configs.
   *
   * @param {ApplicationOptions} options - Application options
   */
  constructor(options) {
    super({
      autoInit: false,
      ui: {
        modules: '[data-component]'
      },
      el: $('html')
    }, options);

    /**
     *
     * @type {ComponentConfig}
     */
    this.config = $.extend(true, {
        homeUrl: '/'
      },
      options.config || {},
      window[Const.APP_CONFIG_VARNAME] || {}
      );

    // initialize user class for auth processes
    if (!this.config.userClass) {
      this.config.userClass = DEFAULT_USER_CLASS;
    }
    /**
     * user instance
     * @type {UserIdentity}
     */
    this.user = new this.config.userClass({
      app: this
    });
    /**
     * A key value object where the key is the component path (dot notation) and value the component instance
     * @type {Object<String,Component>}
     */
    this.components = {};
    /**
     * Contains all access rules
     * @type {Object<RegExp, String>}
     */
    this.access = options.access || {};

    // register components at bootstrap time
    let componentClasses = options.components;
    if (componentClasses) {
      if (Array.isArray(componentClasses)) {
        this.registerComponents(componentClasses);
      }
      else {
        // defined by namespace key
        for (let ns in componentClasses) {
          if (componentClasses.hasOwnProperty(ns)) {
            this.registerComponents(ns, componentClasses[ns]);
          }
        }
      }
    }
  }

  /**
   * Set an already instantiated user class to the application.
   * Should be set in application config 'userClass'.
   * @deprecated
   * @param user
   */
  setUser(user) {
    this.user = user;
  }

  /**
   * Register component classes in the application that will be automaticalle instantiated
   * when requested by a DOM element that sets the data attribute "data-component"
   * @todo "instanceof" check against Component class
   * @param {String} [ns] - namespace, e.g. normally the folder path "js/components/Example.js" -> "components"
   * @param {Array<Component>|Component|Object} imports - component classes to be registered in the main application
   * @return {Application}
   */
  registerComponents(ns, imports){

    if (typeof ns !== "string") {
      imports = ns;
      ns = "";
    }
    else if (ns.charAt(ns.length - 1) !== '.') {
      ns += '.';
    }

    // register classes by array (simple syntax)
    if (Array.isArray(imports)) {
      imports.forEach( clazz => this.components[ ns + clazz.name.toLowerCase() ] = clazz );
    }
    // register single class
    else if (imports.prototype && imports.prototype instanceof BaseClass) {
      this.components[ ns + imports.name.toLowerCase() ] = imports;
    }
    // register classes by classmap (auto-import syntax)
    else {
      for (let className in imports) {
        if (imports.hasOwnProperty(className)) {
          this.components[ ns + className.toLowerCase() ] = imports[className];
        }
      }
    }

    return this;
  }

  /**
   * Returns the home url for the given identity instance or the current authenticated user, if not given.
   * Depends on the application config 'homeUrl'.
   * @param {UserIdentity} [identity=this.user] - any user identity instance or current user
   * @return {string}
   */
  getHomePath(identity = this.user) {
    let home = this.config.homeUrl;
    if (Utils.isFunction(home)) {
      return home.call(this, identity) || '/';
    }
    if (!Utils.isString(home)) {
      home = identity.findRoleInMap(home) || '/';
    }
    return home;
  }

  /**
   * Redirects to the home url.
   */
  toHome() {
    this.redirect(this.getHomePath());
  }

  /**
   * Redirects the user to the login form if configured
   * in the application. Otherwise to home.
   */
  toLogin() {
    sessionStorage.setItem('returnUrl', window.location.pathname + window.location.search);
    if (Utils.isString(this.config.loginUrl)) {
      return this.redirect(this.config.loginUrl);
    }
    this.toHome();
  }

  /**
   * Redirects user by changing location.href to given url.
   * @param {String} url - new URL the user will be redireted to
   */
  redirect(url) {
    window.location.href = url;
  }

  /**
   * Matches user's role against the application access configuration
   * and redirects if permission isn't granted. Using 'toLogin()' for guests
   * and 'toHome()' for authorized user.
   *
   * Returns false if authorization failed and user will be redirected
   * otherwise true.
   * @return {boolean}
   */
  checkPageAccess() {
    let path = location.pathname;

    if (Utils.isFunction(this.access)) {
      if (!this.access.call(this, path, this.user)) {
        this.user.isAuthorized() ? this.toHome() : this.toLogin();
        return false;
      }
      return true;
    }

    if (this.checkPageAccessByMap(this.access, path) === false) {
      this.user.isAuthorized() ? this.toHome() : this.toLogin();
      return false;
    }
    return true;
  }

  /**
   *
   * @param {Object} map - key value map, where key is a path regex and value
   * the user role or array of roles that allows the access.
   * @param {String} path - URI to be checked
   * @return {null|boolean} - true if granted, false if not allowed for path; null if no rule exists for given path
   */
  checkPageAccessByMap(map, path) {
    for (let key in map) {
      let reg = new RegExp(key, 'i');
      if (reg.test(path)) {
        return this.user.matchRole(map[key]);
      }
    }
    return null;
  }

  /**
   * The entry point of the application flow. User's permission for the current page
   * will be checked here 'checkPageAccess'.
   * All components defined in the application DOM will be initialized.
   */
  onInit() {
    /**
     * Array of all component class instances.
     * @type {Array<Component>}
     */
    this.compInstances = [];

    this.user.afterAuth(() => {
      if (this.checkPageAccess()) {
        this.initComponents(this.el);
      }
    });
  }

  /**
   * Searching for module definitions in the given $container and
   * create instances.
   * Prints an error to console when a component class isn't registered that is requested by data-component
   * @param {jQuery} $container - jQuery DOM element where to lookup for data-component attribute
   * @return {Array<Component>} array of module instances
   */
  initComponents($container) {

    let instances = [];
    $container
      .find(this._ui.modules)
      .each((index, el) => {
        let $el = $(el);
        let name = $el.data('component');

        // prevent multiple instances
        if ($el.data('componentInstance')) {
          console.log(`Component '${name}' already instantiated.`);
          return;
        }

        if (this.components[name]) {
          let comp = new this.components[name]({
            app: this,
            el: $el,
          });
          $el.data('componentInstance', comp);
          this.compInstances.push(comp);
          instances.push(comp);
        }
        else {
          console.error(`Component '${name}' is not registered in application.`);
        }
      });
    return instances;
  }

  /**
   * Registers a page exit handler by setting up 'window.onbeforeunload'
   * that informs the user once he leaves the page.
   *
   * @param {string} [message]
   */
  notifyOnExit(message) {
    window.onbeforeunload = function (e) {
      let ev = e || window.event;
      // For IE and Firefox
      if (ev) {
        ev.preventDefault();
        ev.returnValue = message;
      }

      // For Safari
      return message;
    };
  }

  /**
   * Send an email to recipient address with given subject and text
   * by using backend function "sendMessage"
   * @param {String} recipient - recipient's mail address
   * @param {String} subject - email subject
   * @param {String} text - email content
   * @return {Promise}
   */
  sendMessage(recipient, subject, text) {
    return new Request({
      url: '/sendMessage',
      contentType: 'application/x-www-form-urlencoded'
    })
      .post({
        recipient, subject, text
      });
  }

  /**
   * set a Cookie whit name and value. Time is optional and by default 1 Year
   * @todo move to {@link Utils}
   * @param cname
   * @param cvalue
   * @param exdays
   * @returns {boolean}
   */
  setCookie(cname, cvalue, exdays) {
    if(!cname || !cvalue) return false;
    let validDays = exdays?exdays:365;
    let d = new Date();
    d.setTime(d.getTime() + (validDays*24*60*60*1000));
    let expires = "expires="+ d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    return true;
  }

  /**
   * read a Cookie by name
   * @todo move to {@link Utils}
   * @param a
   * @returns {any}
   */
  getCookieValue(a) {
    let b = document.cookie.match('(^|;)\\s*' + a + '\\s*=\\s*([^;]+)');
    return b ? b.pop() : '';
  }

}
